import { useState, useEffect, useContext } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButton,
    SttDialog,
    SttForm,
    SttFormSearch,
    SttMshipList,
} from '../all';
import { useCrud } from '../../lib/hooks';
import { fullName } from '../../lib/utils';
import useUsersService from '../../services/UsersService';
import SttFullPageSpinner from '../spinners/SttFullPageSpinner';

const fetchRows = 8;

type User = {
    id: number | null;
    name: string;
    surname1?: string;
    surname2?: string;
};

type Props = {
    caption: string;
    name: string;
    defVal: User;
    onChange: (user: User) => void;
    onEnterKey?: () => void;
    idSC: number;
    idMship?: number;
};

/**
 * SttSelectOrAddUser: select and existing user obtained from the back-end or manually write the user's name
 *
 * <String> caption: the control's caption
 * <String> name: component name (id)
 * <Object> defVal: currently selected user as {id, name}. id==0 means it's not an existing user
 * <Function> onChange: callback onChange(user => {})
 * <Function> onEnterKey: callback when the 'ENTER' key is pressed
 */
export function SttSelectOrAddPupil({
    caption,
    defVal,
    onChange,
    onEnterKey,
    idSC,
    idMship,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [users, setUsers, , Get] = useCrud<any>();
    const [user, setUser, setProperty] = useCrud<User>(
        typeof defVal === 'object' ? defVal : { id: null, name: defVal || '' },
    );
    const [dialogOpened, setDialogOpened] = useState(false);
    const [newUserDialogOpened, setNewUserDialogOpened] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const createMyScPupilMutation = useUsersService().useCreateMyScPupil({
        onSuccess: (response: any) => {
            setUser({
                id: response?.user?.id,
                name: response?.user?.name,
                surname1: response?.user?.surname1,
                surname2: response?.user?.surname2,
            });
            setNewUserDialogOpened(false);
            setDialogOpened(false);
        },
    });
    const [newUser, setNewUser] = useState({
        name: '',
        surname1: '',
        surname2: '',
    });

    // defVal changes from the outside
    useEffect(() => {
        setUser({
            id: defVal.id,
            name: defVal.name || '',
            surname1: defVal.surname1 || '',
            surname2: defVal.surname2 || '',
        });
    }, [defVal]);

    // Either name or id changed, trigger onChange
    useEffect(() => {
        onChange(user);
    }, [user.name, user.id]);

    // Load 'real' users from the back-end
    function loadUsers(params: Record<string, unknown> = { page: 1 }) {
        Get(`/${idMship ? 'mships' : 'scs'}/${idMship || idSC}/pupils`, {
            ...params,
            name: search,
            rows: fetchRows,
        })
            .then((response: any) => {
                if (response.rows) {
                    setUsers(response);
                }
                if (params.page) {
                    setPage(params.page as number);
                }
            })
            .catch(console.log);
    }

    // Clicking on the search icon
    function onClickSearch() {
        if (!users) {
            loadUsers();
        }
        setDialogOpened(true);
    }

    // Existing user selected
    function selectUser(user: User) {
        setDialogOpened(false);
        if (onChange && user) {
            setUser(user);
            onChange(user);
        }
    }

    function clear() {
        setUser({
            id: null,
            name: '',
            surname1: '',
            surname2: '',
        });
    }
    if (createMyScPupilMutation?.isPending) return <SttFullPageSpinner />;
    return (
        <>
            {/* Search component that also allows the user to write whatever they want */}
            <SttFormSearch
                caption={caption}
                name="name"
                disabled
                defVal={
                    `${fullName(user)}` || ''
                } /* la func. fullName(user) obtiene el fullName o el combo name + surname1 + surname2 tanto de user como de su mship si lo hay */
                onClean={clear}
                onChange={setProperty}
                onClickSearch={onClickSearch}
                onEnterKey={onEnterKey}
            />

            {/* Dialog containing a search form and a list of users */}
            <SttDialog
                title={t('app.usersModal.title')}
                maxWidth="sm"
                onClose={() => setDialogOpened(false)}
                open={dialogOpened}
                content={
                    <div>
                        <Grid
                            container
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Grid size={{ xs: 8 }}>
                                <SttFormSearch
                                    defVal={search}
                                    onClickSearch={() =>
                                        loadUsers({ page: 1, name: search })
                                    }
                                    onChange={({
                                        name,
                                        value,
                                    }: {
                                        name: string | undefined;
                                        value: unknown;
                                    }) => {
                                        if (!name) return;
                                        setSearch(value as string);
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <SttButton
                                    caption={t('buttons.addUser')}
                                    onClick={() => setNewUserDialogOpened(true)}
                                />
                            </Grid>
                        </Grid>

                        <SttMshipList
                            mships={{
                                ...users,
                                rows: users?.rows?.map((row: any) => ({
                                    ...row,
                                    fullName: fullName(row),
                                    img: row.img
                                        ? {
                                              url: cxt?.getResource(
                                                  `/images/${row.img.id}.jpg`,
                                              ),
                                          }
                                        : null,
                                })),
                            }}
                            page={page}
                            onSelect={selectUser}
                            onLoadMships={loadUsers}
                        />

                        <SttDialog
                            title={t('dialogs.createNewUser')}
                            open={newUserDialogOpened}
                            onClose={() => setNewUserDialogOpened(false)}
                            content={
                                <SttForm
                                    onChangeForm={({
                                        name,
                                        value,
                                    }: {
                                        name: string | undefined;
                                        value: unknown;
                                    }) => {
                                        if (!name) return;
                                        setNewUser({
                                            ...newUser,
                                            [name]: value,
                                        });
                                    }}
                                    elements={[
                                        {
                                            xs: 12,
                                            caption: t('user.name'),
                                            name: 'name',
                                            value: newUser.name,
                                        },
                                        {
                                            xs: 12,
                                            caption: t('user.surname1'),
                                            name: 'surname1',
                                            value: newUser.surname1,
                                        },
                                        {
                                            xs: 12,
                                            caption: t('user.surname2'),
                                            name: 'surname2',
                                            value: newUser.surname2,
                                        },
                                    ]}
                                />
                            }
                            buttons={[
                                {
                                    type: 'accept',
                                    disabled:
                                        newUser.name === '' ||
                                        newUser.surname1 === '',
                                    caption: t('common.save'),
                                    onClick: () => {
                                        createMyScPupilMutation.mutate({
                                            scId: idSC,
                                            params: {
                                                name: newUser?.name,
                                                surname1: newUser?.surname1,
                                                surname2: newUser?.surname2,
                                            },
                                        });
                                    },
                                },
                            ]}
                        />
                    </div>
                }
            />
        </>
    );
}
